import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Settings.js BillingTab
//     Button label is "💳 Manage subscription & invoices" (not "Manage
//     billing"). Past-due state shows a red banner.
//   - supabase/functions/create-billing-portal/index.ts
//     Standard Stripe Billing Portal session. Features available (cancel,
//     payment method, invoices) depend on Stripe dashboard config but the
//     header comment confirms those four are intended to be on.
//   - src/pages/Terms.js line 163: "prices ... are exclusive of any VAT
//     that may apply". So invoices do NOT include VAT at the standard
//     UK rate as the old article claimed.
//   - No automatic_tax config; no tax_rate setup found in functions.
// Removed:
//   - "Manage billing" button label (actual: "Manage subscription &
//     invoices")
//   - "Settings -> Practice -> Branding" path (actual: Settings -> Profile)
//   - "Receipt Chaser invoices include VAT at the standard UK rate" --
//     terms say prices are exclusive of VAT. Removed VAT claim entirely
//     since the actual treatment isn't clear from code, and getting it
//     wrong in either direction misleads accountants.
//   - "90-day grace period" after failed payment -- same fabrication;
//     doesn't exist
//   - Specific retry schedule "3 retries over 3-4 days" -- Stripe's
//     Smart Retries vary by configuration; presenting specific numbers
//     as fact is misleading. Replaced with "over several days".
// Pass 2 added:
//   - Past-due banner in Billing tab (failed payment indicator inside
//     Receipt Chaser, separate from Stripe's emails)
//   - "Resubscribe" path if access has fully ended

export default {
  title:   'Updating your payment method',
  summary: 'Changing your card or billing details, downloading invoices, and what happens if a payment fails.',
  body: (
    <>
      <P>
        Receipt Chaser uses Stripe to handle billing. Your card
        details live with Stripe, never on our servers, so card
        management happens through their secure customer portal,
        which we present as the Invoice Portal. You get to it from
        a single button inside your account settings.
      </P>

      <H>Opening the Invoice Portal</H>
      <OL>
        <Li>Go to <Code>Settings → Billing</Code>.</Li>
        <Li>
          Click <Code>💳 Manage subscription &amp; invoices</Code>.
          The Invoice Portal opens.
        </Li>
        <Li>
          From there you can update your card, change your billing
          address, view past invoices, and cancel your subscription.
          When you're done, close the tab or use the link back to
          Receipt Chaser.
        </Li>
      </OL>

      <Callout variant="info" title="Owner only">
        The Billing tab is only visible to the practice owner.
        Admins and Assistants don't see it. If you're not the owner,
        ask whoever set up the account.
      </Callout>

      <H>Updating or replacing your card</H>
      <P>
        Inside the Invoice Portal, find the <strong>Payment
        method</strong> section. To replace your card cleanly:
      </P>
      <OL>
        <Li>Add the new card.</Li>
        <Li>Set it as the default.</Li>
        <Li>Remove the old one.</Li>
      </OL>
      <P>
        Doing it in that order avoids any window where there's no
        valid card on file.
      </P>

      <Callout variant="warning" title="Why we can't change the card for you">
        Card numbers and security codes never reach Receipt Chaser.
        They go straight from your browser to our payment provider.
        We see only the card brand and last four digits, the same as
        you'd see on a receipt. If you ask us to put a new card on
        the account via support, we can't do it for you. You need
        to enter the details into the Invoice Portal yourself.
      </Callout>

      <H>Updating billing address or business name</H>
      <P>
        The Invoice Portal also lets you update the billing address
        and business name that appear on your invoices. These details
        are separate from the practice name shown on client-facing
        emails (which you set in <Code>Settings → Profile</Code>).
      </P>

      <H>Downloading past invoices</H>
      <P>
        Every monthly invoice is available as a PDF from the Invoice
        Portal, going back to the start of your subscription. Useful
        for your own bookkeeping or for claiming Receipt Chaser as a
        business expense.
      </P>
      <OL>
        <Li>Open <Code>💳 Manage subscription &amp; invoices</Code>.</Li>
        <Li>Scroll to the Invoice history section.</Li>
        <Li>Click any invoice to view or download it as a PDF.</Li>
      </OL>

      <H>What happens when a payment fails</H>
      <P>
        Card payments fail for plenty of reasons that aren't your
        fault: an expired card, an issuer flagging a recurring
        charge as suspicious, or a bank's fraud system getting
        twitchy. When it happens:
      </P>
      <OL>
        <Li>
          <strong>You get an email.</strong> Our payment provider
          sends a notification including a direct link to update
          your card.
        </Li>
        <Li>
          <strong>Receipt Chaser shows a banner.</strong> Inside the
          app, the Billing tab shows a red "Payment overdue" badge
          and a banner asking you to update your payment method. The
          dashboard otherwise works normally.
        </Li>
        <Li>
          <strong>We retry automatically.</strong> Over the next two
          weeks, we re-attempt the charge to give you time to fix
          the card.
        </Li>
        <Li>
          <strong>After 2 weeks of failed payments, your subscription
          is automatically cancelled</strong> and you'll lose access
          at the end of your paid period. Signing in then shows a
          "Subscription has ended" lock screen with a Resubscribe
          option. Your data is preserved while the account exists.
          See{' '}
          <Link to="/support/changing-or-cancelling-your-plan">Changing or cancelling your plan</Link>
          {' '}for more on resubscribing.
        </Li>
      </OL>

      <Callout variant="tip" title="Fastest fix for a failed payment">
        Click the link in the failed-payment email. It takes you
        straight to a form to update your card. The charge is then
        re-attempted with the new card.
      </Callout>

      <H>VAT and invoices</H>
      <P>
        Receipt Chaser's prices (£3/client/month, £25/month flat) are
        quoted exclusive of VAT, per our{' '}
        <Link to="/terms">Terms</Link>. If you need a specific VAT
        treatment for your accounting (such as your VAT number on the
        invoice), check the relevant settings inside the Invoice
        Portal or speak to your accountant. We're not able to
        provide tax advice.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/changing-or-cancelling-your-plan">Changing or cancelling your plan</Link>
        </Li>
        <Li>
          <Link to="/support/managing-practice-users">Managing practice users</Link>
        </Li>
      </UL>
    </>
  ),
}
